"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const steps = [{
  number: "01",
  title: "Data discovery",
  duration: "— 14 DAYS",
  description: "We audit your digital footprint, identifying high-value signals and untapped market segments to build a foundation of pure performance.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701027/img/data_discovery_phase.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701027/img/data_discovery_phase.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701027/img/data_discovery_phase.png-wvc-sizes",
  alt: "Digital data streams being analyzed"
}, {
  number: "02",
  title: "Strategy synthesis",
  duration: "— 8 HOURS",
  description: "In our command center, we align creative assets with algorithmic precision, mapping the path to maximum conversion and ROI.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701027/img/strategy_synthesis_phase.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701027/img/strategy_synthesis_phase.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701027/img/strategy_synthesis_phase.webp-wvc-sizes",
  alt: "Strategic planning session on digital displays"
}, {
  number: "03",
  title: "Campaign ignition",
  duration: "— 21 DAYS",
  description: "We deploy high-velocity ad sets across targeted channels, iterating in real-time to capture market share and drive engagement.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701027/img/campaign_ignition_phase.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701027/img/campaign_ignition_phase.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701027/img/campaign_ignition_phase.png-wvc-sizes",
  alt: "Digital campaign launch dashboard"
}, {
  number: "04",
  title: "Conversion optimization",
  duration: "— 5 DAYS",
  description: "We refine landing experiences and funnel logic, stripping away friction to ensure every click converts into a measurable business outcome.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701027/img/conversion_optimization_phase.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701027/img/conversion_optimization_phase.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701027/img/conversion_optimization_phase.png-wvc-sizes",
  alt: "Conversion rate optimization metrics"
}, {
  number: "05",
  title: "Performance scaling",
  duration: "— 28 DAYS",
  description: "We amplify winning creative and budget allocation, scaling your growth engine while maintaining strict efficiency benchmarks.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701027/img/performance_scaling_phase.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701027/img/performance_scaling_phase.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701027/img/performance_scaling_phase.webp-wvc-sizes",
  alt: "Scaling performance charts and graphs"
}, {
  number: "06",
  title: "Revenue realization",
  duration: "— THE MOMENT",
  description: "Delivered through transparent reporting and high-impact results, the final phase is where your brand transformation becomes your new reality.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701027/img/revenue_realization_phase.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701027/img/revenue_realization_phase.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1701027/img/revenue_realization_phase.png-wvc-sizes",
  alt: "Final revenue growth report"
}];
function RevealWords({
  text,
  className
}: {
  text: string;
  className?: string;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15,
    once: true
  });
  const words = text.split(" ");
  return <div ref={ref} className={className}>
      {words.map((word, i) => <span key={i} data-index={i} className="inline-block transition-all duration-700 ease-out mr-[0.25em]" style={{
      opacity: isVisible ? 1 : 0,
      transform: isVisible ? "translateY(0)" : "translateY(20px)",
      transitionDelay: `${i * 40}ms`
    }}>
          {word}
        </span>)}
    </div>;
}
function StepImage({
  src,
  srcset,
  sizes,
  alt
}: {
  src: string;
  srcset: string;
  sizes: string;
  alt: string;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2,
    once: true
  });
  return <div ref={ref} className="relative overflow-hidden rounded-lg aspect-[4/5] bg-muted" style={{
    opacity: isVisible ? 1 : 0,
    transition: "opacity 1200ms ease-out"
  }}>
      <img src={src} data-wvc-srcset={srcset} data-wvc-sizes={sizes} alt={alt} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out" style={{
      transform: isVisible ? "scale(1)" : "scale(1.04)"
    }} />
      <div className="pointer-events-none absolute inset-0 opacity-[0.08] mix-blend-overlay" style={{
      backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")"
    }} />
    </div>;
}
export default function TheGrowthFormula() {
  return <section data-section-id="4206" id="the-growth-formula" className="bg-background text-foreground pt-32 md:pt-40 lg:pt-[160px] pb-24 md:pb-32 overflow-hidden">
      <div className="max-w-[1280px] mx-auto px-6 md:px-10">
        {}
        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-12 md:col-start-2 md:col-span-10 text-center">
            <p className="font-mono text-xs md:text-sm tracking-[0.2em] uppercase text-primary mb-8 md:mb-10">
              — The Methodology / Vol. 01
            </p>
            <h2 className="font-sans font-default font-extrabold leading-[1.02] tracking-tighter text-foreground text-5xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-[112px]">
              <RevealWords text="The growth" className="block" />
              <span className="block italic font-extrabold text-primary">
                <RevealWords text="formula" />
              </span>
            </h2>
            <p className="mt-10 md:mt-12 max-w-[620px] mx-auto font-default text-base md:text-lg lg:text-[19px] leading-relaxed text-muted-foreground">
              At Catalyst Growth Lab, we treat marketing as a high-velocity chemical reaction. Our methodology combines data-driven precision with aggressive creative strategy to transform your brand into a market-leading performance engine.
            </p>
          </div>
        </div>

        {}
        <div className="relative mt-32 md:mt-40 lg:mt-[160px]">
          {}
          <div aria-hidden="true" className="hidden md:block absolute left-1/2 top-0 bottom-0 -translate-x-1/2 w-px bg-border" />

          <div className="flex flex-col gap-24 md:gap-28 lg:gap-[120px]">
            {steps.map((step, i) => {
            const isOdd = i % 2 === 0;
            return <div key={step.number} data-index={i} className="relative grid grid-cols-12 gap-6 md:gap-8 items-center">
                  {}
                  <div aria-hidden="true" className="hidden md:block absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 bg-primary z-10" />

                  {}
                  <div className={`col-span-12 md:col-span-5 ${isOdd ? "md:col-start-1" : "md:col-start-8"}`}>
                    <StepImage src={step.image} srcset={step.srcset} sizes={step.sizes} alt={step.alt} />
                  </div>

                  {}
                  <div className={`col-span-12 md:col-span-5 ${isOdd ? "md:col-start-7" : "md:col-start-2 md:row-start-1"}`}>
                    <div className="flex items-start gap-4 md:gap-6 mb-4 md:mb-6">
                      <span aria-hidden="true" className="font-mono font-default leading-none text-[88px] md:text-[110px] lg:text-[140px] tracking-tighter text-transparent" style={{
                    WebkitTextStroke: "1px hsl(240 5% 15%)"
                  }}>
                        {step.number}
                      </span>
                      <div className="pt-2 md:pt-4 flex-1">
                        <h3 className="font-sans font-default font-extrabold text-2xl sm:text-3xl md:text-4xl lg:text-[40px] leading-tight text-foreground">
                          <RevealWords text={step.title} />
                        </h3>
                      </div>
                    </div>

                    <p className="font-mono text-xs md:text-sm tracking-[0.2em] uppercase text-primary mb-5 md:mb-6">
                      {step.duration}
                    </p>

                    <div className="max-w-[440px] font-default text-base md:text-[17px] leading-relaxed text-muted-foreground">
                      <RevealWords text={step.description} />
                    </div>
                  </div>
                </div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}